import { Box, Card, CardContent, CardMedia, Typography, makeStyles } from "@material-ui/core";
import clsx from "clsx";
import * as React from "react";

import { textStyles } from "@/designTokens";
import theme from "@/theme";
import { calculateYieldRate } from "@/utilities/yield";

import NoImage from "@assets/image/NoImage.svg";
import PropertySampleImage01 from "@assets/image/property_sample_image01.png";
import PropertySampleImage02 from "@assets/image/property_sample_image02.png";
import PropertySampleImage03 from "@assets/image/property_sample_image03.png";

const useStyles = makeStyles(() => ({
  card: {
    minWidth: 200,
    maxWidth: 200,
    flexShrink: 0,
    cursor: "default",
    whiteSpace: "normal",
    display: "flex",
    flexDirection: "column",
    boxShadow: "none",
    borderRadius: 0,
  },
  media: {
    height: 140,
    padding: theme.spacing(1),
    borderTopRightRadius: 40,
    borderBottomRightRadius: 40,
  },
  propertyDetails: {
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    textAlign: "left",
    padding: theme.spacing(1),
  },
  incomeSection: {},
  propertyName: {
    wordBreak: "break-word", // 長い語を折り返す
    overflowWrap: "break-word", // Safari対策
    lineHeight: "1.5em",
  },
  propertyArea: {
    wordBreak: "break-word", // 長い語を折り返す
    overflowWrap: "break-word", // Safari対策
    lineHeight: "1.5em",
  },
  rentLabel: {
    marginTop: theme.spacing(0.5),
  },
  income: {},
  // プランナー詳細用：「家賃収入 80,000 円 / 月」を1行で出す（オファー画面は2行のまま）
  incomeInline: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "baseline",
    columnGap: 4,
    margin: 0,
  },
  incomeInlineLabel: { fontSize: 12, lineHeight: 1.5, color: theme.palette.text.secondary },
  incomeInlineAmount: { ...textStyles["Number/S"], color: theme.palette.semantic["text/primary"] },
  incomeInlineUnit: { fontSize: 12, lineHeight: 1.5, color: theme.palette.text.secondary },
  limitedCard: {
    minWidth: 200,
    maxWidth: 200,
    overflow: "hidden",
    cursor: "pointer",
  },
  limitedMedia: {
    height: 140,
    borderRadius: theme.spacing(0.5),
  },
  limitedContent: {
    padding: theme.spacing(1),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  limitedTitleRow: {
    display: "flex",
    flexWrap: "nowrap",
  },
  limitedPriceWrapper: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "flex-end",
    gap: theme.spacing(0.25),
  },
  limitedPropertyName: {
    fontWeight: 700,
    fontSize: 14,
    lineHeight: 1.3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  limitedPrice: {
    color: theme.palette.secondary.main,
    fontWeight: 700,
    fontSize: 18,
    lineHeight: 1,
  },
  limitedPriceUnit: {
    color: theme.palette.secondary.main,
    fontWeight: 700,
    fontSize: 12,
    lineHeight: 1,
  },
  limitedInfoRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
  },
  limitedInfoLabel: {
    minWidth: 56,
    backgroundColor: theme.palette.grey[100],
    color: theme.palette.text.secondary,
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 1.3,
    padding: theme.spacing(0.5),
    textAlign: "center",
  },
  limitedInfoValue: {
    color: theme.palette.text.secondary,
    fontWeight: 400,
    fontSize: 12,
    lineHeight: 1.3,
    textAlign: "left",
    wordBreak: "break-word",
    flex: 1,
  },
}));

interface props {
  profileImagePath: string;
  propertyName: string;
  propertyArea?: string | null;
  propertyIncome?: number | null;
  propertyPrice?: number | null;
  variant?: "default" | "limited";
  // default のときの家賃収入の並び。inline はラベルと金額を1行にする（プランナー詳細だけで使う）
  incomeLayout?: "stacked" | "inline";
  fullWidth?: boolean;
}

export const SalesAchievementsCard: React.FC<props> = ({
  profileImagePath,
  propertyName,
  propertyArea,
  propertyIncome,
  propertyPrice,
  variant = "default",
  incomeLayout = "stacked",
  fullWidth = false,
}) => {
  const classes = useStyles();

  const formatMonthlyIncome = (income?: number | null, options?: { compact?: boolean }) => {
    if (income === null || income === undefined) return "-";
    return options?.compact ? `${income.toLocaleString()}円/月` : `${income.toLocaleString()} 円 / 月`;
  };

  const formatLimitedPriceParts = (price?: number | null) => {
    if (price === null || price === undefined) {
      return { amount: "-", unit: "" };
    }
    const manValue = price / 10000;
    const hasFraction = !Number.isInteger(manValue);
    const amount = manValue.toLocaleString(undefined, {
      maximumFractionDigits: hasFraction ? 1 : 0,
      minimumFractionDigits: hasFraction ? 1 : 0,
    });
    return { amount, unit: "万円" };
  };

  // imageが空の場合は画像3枚からランダムで表示
  const sampleImages = [PropertySampleImage01, PropertySampleImage02, PropertySampleImage03];
  const selectedImage =
    profileImagePath !== NoImage && profileImagePath !== ""
      ? profileImagePath
      : sampleImages[Math.floor(Math.random() * sampleImages.length)];

  if (variant === "limited") {
    const limitedPriceParts = formatLimitedPriceParts(propertyPrice);

    return (
      <Card
        className={clsx(classes.card, classes.limitedCard)}
        style={fullWidth ? { minWidth: "100%", maxWidth: "100%", width: "100%" } : undefined}
      >
        <CardMedia className={classes.limitedMedia} image={selectedImage} title={propertyName || "Property Image"} />
        <CardContent className={classes.limitedContent}>
          <Box className={classes.limitedTitleRow}>
            <Typography className={classes.limitedPropertyName}>{propertyName || "-"}</Typography>
          </Box>
          <Box className={classes.limitedPriceWrapper}>
            <Typography className={classes.limitedPrice}>{limitedPriceParts.amount}</Typography>
            {limitedPriceParts.unit && (
              <Typography className={classes.limitedPriceUnit}>{limitedPriceParts.unit}</Typography>
            )}
          </Box>
          <Box className={classes.limitedInfoRow}>
            <Box className={classes.limitedInfoLabel}>所在地</Box>
            <Typography className={classes.limitedInfoValue}>{propertyArea || "-"}</Typography>
          </Box>
          <Box className={classes.limitedInfoRow}>
            <Box className={classes.limitedInfoLabel}>家賃収入</Box>
            <Typography className={classes.limitedInfoValue}>
              {formatMonthlyIncome(propertyIncome, { compact: true })}
            </Typography>
          </Box>
          <Box className={classes.limitedInfoRow}>
            <Box className={classes.limitedInfoLabel}>利回り</Box>
            <Typography className={classes.limitedInfoValue}>
              {calculateYieldRate(propertyIncome, propertyPrice)}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={classes.card}>
      <CardMedia className={classes.media} image={selectedImage} title="Property Image" />
      <CardContent className={classes.propertyDetails}>
        <Box className={classes.incomeSection} mb={1}>
          {incomeLayout === "inline" ? (
            <Typography component="p" className={classes.incomeInline} data-testid="sales-achievement-income">
              <span className={classes.incomeInlineLabel}>家賃収入</span>
              {propertyIncome === null || propertyIncome === undefined ? (
                <span className={classes.incomeInlineAmount}>-</span>
              ) : (
                <>
                  <span className={classes.incomeInlineAmount}>{propertyIncome.toLocaleString()}</span>
                  {/* 月額であることを伝えるため「/ 月」は残す */}
                  <span className={classes.incomeInlineUnit}>円 / 月</span>
                </>
              )}
            </Typography>
          ) : (
            <>
              {/* 家賃収入 */}
              <Typography variant="body2" className={classes.rentLabel}>
                家賃収入
              </Typography>

              {/* 金額 */}
              <Typography variant="body1" className={classes.income}>
                {formatMonthlyIncome(propertyIncome)}
              </Typography>
            </>
          )}
        </Box>
        {/* 物件名 */}
        <Typography variant="body2" className={classes.propertyName}>
          {propertyName}
        </Typography>

        {/* エリア情報 */}
        <Typography variant="caption" color="textSecondary" className={classes.propertyArea}>
          {propertyArea}
        </Typography>
      </CardContent>
    </Card>
  );
};
